import React, { useState, useMemo, useCallback, useEffect } from 'react';
import useSuggestionManage from '@/hooks/useSuggestionManage';
import { message, Modal, Input, Table, Pagination } from 'starry-sky-ui';
import type { ColumnType } from 'starry-sky-ui';
import type { SuggestionItem } from '@/types/admin';
import './index.scss';

const Suggestion: React.FC = () => {
  const { data: allData, total, loading, keyword, setKeyword, page, setPage, pageSize, setPageSize, fetchData, deleteSuggestion } = useSuggestionManage();

  // ---- 搜索 ----
  const [searchKeyword, setSearchKeyword] = useState('');

  const handleSearch = useCallback(() => {
    setPage(1);
    setKeyword(searchKeyword.trim());
  }, [searchKeyword, setKeyword, setPage]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // 搜索字变化时重置分页
  useEffect(() => {
    setPage(1);
  }, [keyword, setPage]);

  // ---- 删除确认 ----
  const [deleteTarget, setDeleteTarget] = useState<SuggestionItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await deleteSuggestion(deleteTarget.id);
      message.success('建议删除成功！');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, deleteSuggestion]);

  // ---- 列定义 ----
  const columns: ColumnType[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      render: (val: string) => (
        <span className="suggestion-cell-content">{val}</span>
      ),
    },
    {
      title: '提交时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => {
        if (!val) return '-';
        return new Date(val).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, record: SuggestionItem) => (
        <button
          className="suggestion-action-btn delete"
          onClick={() => setDeleteTarget(record)}
        >
          删除
        </button>
      ),
    },
  ];

  const handlePageChange = useCallback(
    (p: number, ps: number) => {
      setPage(p);
      setPageSize(ps);
    },
    [setPage, setPageSize],
  );

  return (
    <div className="suggestion-container">
      <div className="suggestion-header">
        <h1 className="suggestion-title">建议反馈管理</h1>
      </div>

      {/* 搜索栏 */}
      <div className="suggestion-filters">
        <div className="suggestion-search-group">
          <Input
            placeholder="搜索用户 / 内容..."
            value={searchKeyword}
            onChange={setSearchKeyword}
            onKeyDown={handleSearchKeyDown}
            allowClear
            size="middle"
            className="suggestion-search-input"
          />
          <button className="suggestion-search-btn" onClick={handleSearch}>
            搜索
          </button>
        </div>
        <span className="suggestion-count">共 {total} 条建议</span>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={allData}
        rowKey={(record: SuggestionItem) => String(record.id!)}
        loading={loading}
        size="middle"
        bordered
        pagination={false}
        emptyText="暂无建议"
      />

      {/* 分页 */}
      {total > 0 && (
        <div className="suggestion-pagination-wrap">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal
            showSizeChanger
            pageSizeOptions={[10, 20, 30, 50]}
            onChange={handlePageChange}
          />
        </div>
      )}

      {/* 删除确认弹窗 */}
      <Modal
        open={deleteTarget !== null}
        title="确认删除"
        onOk={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        confirmLoading={deleteLoading}
        okText="删除"
        cancelText="取消"
      >
        <p>
          确定要删除 <strong>{deleteTarget?.name}</strong> 的建议吗？
        </p>
      </Modal>
    </div>
  );
};

export default Suggestion;
